import { Controller } from "@hotwired/stimulus"

/**
 * Generic Slide-Over Controller
 *
 * Shows/hides a slide-over panel and loads content via turbo-frame.
 *
 * Usage:
 *   <div data-controller="slide-over">
 *     <button data-action="click->slide-over#open"
 *             data-slide-over-url-param="/path/to/content">Open</button>
 *
 *     <div data-slide-over-target="overlay">...</div>
 *     <div data-slide-over-target="panel">
 *       <turbo-frame data-slide-over-target="frame">...</turbo-frame>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["panel", "overlay", "frame"]
  static values = {
    open: { type: Boolean, default: false },
    autoOpenUrl: { type: String, default: "" }
  }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.handlePopstate = this.handlePopstate.bind(this)
    this.handleCloseEvent = this.handleCloseEvent.bind(this)
    document.addEventListener("keydown", this.handleEscape)
    window.addEventListener("popstate", this.handlePopstate)
    window.addEventListener("slide-over:close", this.handleCloseEvent)

    // Auto-open if URL is provided, but not if show_cart modal will be shown
    const url = new URL(window.location)
    const showCartWillOpen = url.searchParams.get("show_cart") === "true"

    if (this.autoOpenUrlValue && !showCartWillOpen) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        if (this.hasFrameTarget) {
          this.frameTarget.src = this.autoOpenUrlValue
        }
        this.showPanel()
      })
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    window.removeEventListener("popstate", this.handlePopstate)
    window.removeEventListener("slide-over:close", this.handleCloseEvent)
  }

  handleCloseEvent() {
    if (this.openValue) {
      this.close()
    }
  }

  open(event) {
    event?.preventDefault()

    // Get URL from the clicked element's data attribute
    const url = event?.params?.url
    if (url && this.hasFrameTarget) {
      this.frameTarget.src = url
      this.updateBrowserUrl(url)
    }

    this.showPanel()
  }

  close(event) {
    event?.preventDefault()
    this.clearBrowserUrl()
    this.hidePanel()
  }

  toggle(event) {
    event?.preventDefault()
    this.openValue ? this.hidePanel() : this.showPanel()
  }

  showPanel() {
    this.panelTarget.classList.remove("translate-x-full")
    this.overlayTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      this.overlayTarget.classList.remove("opacity-0")
      this.overlayTarget.classList.add("opacity-100")
    })

    document.body.classList.add("overflow-hidden")
    this.openValue = true
  }

  hidePanel() {
    this.panelTarget.classList.add("translate-x-full")
    this.overlayTarget.classList.remove("opacity-100")
    this.overlayTarget.classList.add("opacity-0")

    setTimeout(() => {
      this.overlayTarget.classList.add("hidden")
    }, 300)

    document.body.classList.remove("overflow-hidden")
    this.openValue = false
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.openValue) {
      this.close()
    }
  }

  handlePopstate() {
    const url = new URL(window.location)
    if (!url.searchParams.has("room") && this.openValue) {
      // URL no longer has room param, close the panel
      this.hidePanel()
    } else if (url.searchParams.has("room") && !this.openValue) {
      // URL has room param but panel is closed, open it
      const roomId = url.searchParams.get("room")
      const contentUrl = `/search/room_detail/${roomId}`
      if (this.hasFrameTarget) {
        this.frameTarget.src = contentUrl
      }
      this.showPanel()
    }
  }

  closeOnBackdrop(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  // Touch swipe support for mobile
  touchStart(event) {
    this.touchStartX = event.touches[0].clientX
  }

  touchMove(event) {
    if (!this.touchStartX) return

    const diff = event.touches[0].clientX - this.touchStartX

    if (diff > 50) {
      this.close()
      this.touchStartX = null
    }
  }

  touchEnd() {
    this.touchStartX = null
  }

  // URL management for deep linking
  updateBrowserUrl(contentUrl) {
    // Extract prefix_id from URL like /search/room_detail/service_abc123
    const match = contentUrl.match(/room_detail\/([^/?]+)/)
    if (match) {
      const prefixId = match[1]
      const url = new URL(window.location)
      url.searchParams.set("room", prefixId)
      history.pushState({}, "", url)
    }
  }

  clearBrowserUrl() {
    const url = new URL(window.location)
    if (url.searchParams.has("room")) {
      url.searchParams.delete("room")
      history.pushState({}, "", url)
    }
  }
}
